import React, { useCallback, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import SEOMeta from '../components/SEOMeta';
import { API_BASE } from '../config/api';
import { getProductImages } from '../utils/productImages';

function StarRating({ value, onChange, readOnly }) {
  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          className={`star${star <= value ? ' filled' : ''}`}
          onClick={() => !readOnly && onChange && onChange(star)}
          disabled={readOnly}
          aria-label={`${star} star`}
        >{'\u2605'}</button>
      ))}
    </div>
  );
}

function getAverageRating(reviews, product) {
  if (reviews.length) {
    const sum = reviews.reduce((total, review) => total + Number(review.rating || 0), 0);
    return sum / reviews.length;
  }
  return Number(product?.average_rating || 0);
}

function getReviewCount(reviews, product) {
  return reviews.length || Number(product?.review_count || 0);
}

function formatPrice(price) {
  const value = Number(price || 0);
  return value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [toast, setToast] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, text: '' });
  const [reviewError, setReviewError] = useState('');
  const DESC_LIMIT = 300;

  const { addToCart } = useCart();

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setToast(`${product.name} has been added to cart`);
  };

  const handlePurchaseNow = () => {
    navigate('/checkout', { state: { product, quantity } });
  };

  const refreshProductRating = useCallback((nextReviews) => {
    if (!nextReviews.length) return;

    const averageRating = nextReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / nextReviews.length;
    setProduct(current => current ? {
      ...current,
      average_rating: Number(averageRating.toFixed(1)),
      review_count: nextReviews.length,
    } : current);
  }, []);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name.trim() || !reviewForm.text.trim()) {
      setReviewError('Please fill in your name and review.');
      return;
    }

    setReviewError('');
    setReviewSubmitting(true);

    try {
      const lookup = product.slug || product.id || slug;
      const response = await fetch(`${API_BASE}/products/${lookup}/reviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          data?.detail ||
          data?.name?.[0] ||
          data?.text?.[0] ||
          data?.rating?.[0] ||
          'We could not submit your review. Please try again.';
        throw new Error(message);
      }

      const updatedReviews = [data, ...reviews];
      setReviews(updatedReviews);
      refreshProductRating(updatedReviews);
      setReviewForm({ name: '', rating: 5, text: '' });
    } catch (err) {
      setReviewError(err.message || 'We could not submit your review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    setError(null);
    setProduct(null);
    setReviews([]);
    setRelated([]);
    setActiveImage(0);
    setDescExpanded(false);

    fetch(`${API_BASE}/products/${slug}/`)
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        setProduct(data);
        setLoading(false);

        if (data.slug && data.slug !== slug) {
          navigate(`/product/${data.slug}`, { replace: true });
        }
      })
      .catch(() => {
        if (ignore) return;
        setError('Product not found.');
        setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [slug, navigate]);

  useEffect(() => {
    if (!product?.id) return undefined;
    let ignore = false;
    const lookup = product.slug || product.id;

    setReviewsLoading(true);
    fetch(`${API_BASE}/products/${lookup}/reviews/`)
      .then(res => {
        if (!res.ok) throw new Error('Reviews unavailable');
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        const nextReviews = Array.isArray(data) ? data : [];
        setReviews(nextReviews);
        refreshProductRating(nextReviews);
      })
      .catch(() => {
        if (!ignore) setReviews([]);
      })
      .finally(() => {
        if (!ignore) setReviewsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [product?.id, product?.slug, refreshProductRating]);

  useEffect(() => {
    if (!product?.category) return;
    fetch(`${API_BASE}/categories-with-products/`)
      .then(r => r.json())
      .then(data => {
        const cat = data.find(c => c.id === product.category);
        if (cat) setRelated(cat.products.filter(p => p.id !== product.id).slice(0, 4));
      })
      .catch(() => {});
  }, [product?.category, product?.id]);

  if (loading) return <div className="status-msg">Loading product...</div>;
  if (error) return <div className="status-msg error">{error} <Link to="/">Back to Home</Link></div>;

  const images = getProductImages(product);
  const avgRating = getAverageRating(reviews, product);
  const reviewCount = getReviewCount(reviews, product);

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || `Buy ${product.name} online at Hercles Global.`,
    image: images.length ? images : undefined,
    sku: String(product.id),
    brand: product.category_name ? { '@type': 'Brand', name: product.category_name } : undefined,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'KES',
      price: Number(product.price || 0).toFixed(2),
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'Hercles Global' },
      url: `https://herclesglobal.com/product/${product.slug || product.id}`,
    },
    ...(avgRating > 0 && reviewCount > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    } : {}),
  };

  const productBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://herclesglobal.com/' },
      ...(product.category_name && product.category_slug ? [{ '@type': 'ListItem', position: 2, name: product.category_name, item: `https://herclesglobal.com/category/${product.category_slug}` }] : []),
      { '@type': 'ListItem', position: product.category_name && product.category_slug ? 3 : 2, name: product.name, item: `https://herclesglobal.com/product/${product.slug || product.id}` },
    ],
  };

  const truncatedDesc = product.description
    ? product.description.replace(/\s+/g, ' ').trim().slice(0, 155) + (product.description.length > 155 ? '...' : '')
    : `Buy ${product.name} online at Hercles Global. Best price, fast delivery.`;

  return (
    <div className="detail-page">
      <SEOMeta
        title={`${product.name} - Buy Online in Kenya`}
        description={truncatedDesc}
        canonical={`/product/${product.slug || product.id}`}
        ogImage={images[0] || undefined}
        type="product"
        jsonLd={[productJsonLd, productBreadcrumb]}
      />
      <Header backTo="/" backLabel="<- Back to Products" />

      <div className="detail-container">
        <div className="detail-images">
          <div className="detail-main-image">
            {images.length > 0 ? (
              <img src={images[activeImage]} alt={product.name} loading="lazy" />
            ) : (
              <div className="product-image-placeholder">No Image Available</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="detail-thumbnails">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${product.name} ${i + 1}`}
                  className={`detail-thumb${activeImage === i ? ' active' : ''}`}
                  onClick={() => setActiveImage(i)}
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>

        <div className="detail-info">
          {product.category_name && (
            <p className="detail-category">{product.category_name}</p>
          )}
          <h1 className="detail-name">{product.name}</h1>

          {avgRating > 0 && reviewCount > 0 && (
            <div className="detail-rating-row">
              <StarRating value={Math.round(avgRating)} readOnly />
              <span className="detail-rating-count">{avgRating.toFixed(1)} ({reviewCount} review{reviewCount !== 1 ? 's' : ''})</span>
            </div>
          )}

          {product.description && (
            <div className="detail-description-wrap">
              <p className="detail-description">
                {descExpanded || product.description.length <= DESC_LIMIT
                  ? product.description
                  : `${product.description.slice(0, DESC_LIMIT)}...`}
              </p>
              {product.description.length > DESC_LIMIT && (
                <button className="read-more-btn" onClick={() => setDescExpanded(value => !value)}>
                  {descExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}
            </div>
          )}

          <p className="detail-price">KES {formatPrice(product.price)}</p>

          <div className="trust-badges">
            <span className="trust-badge"><span className="trust-icon">{'\u2713'}</span> Free delivery on orders over KES 5,000</span>
            <span className="trust-badge"><span className="trust-icon">{'\u2713'}</span> Secure payment - card &amp; M-Pesa</span>
            <span className="trust-badge"><span className="trust-icon">{'\u2713'}</span> 30-day hassle-free returns</span>
          </div>

          <div className="detail-order">
            <div className="detail-qty">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
            <button className="cart-btn" onClick={handleAddToCart}>Add to Cart</button>
            <button className="order-btn" onClick={handlePurchaseNow}>Buy Now</button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="related-section">
          <h2 className="related-title">You May Also Like</h2>
          <div className="hp-product-grid">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      <div className="reviews-section">
        <h2 className="reviews-title">
          Customer Reviews
          {avgRating > 0 && reviewCount > 0 && (
            <span className="reviews-avg-inline">
              <StarRating value={Math.round(avgRating)} readOnly />
              <span>{avgRating.toFixed(1)} out of 5 - {reviewCount} review{reviewCount !== 1 ? 's' : ''}</span>
            </span>
          )}
        </h2>

        <div className="review-form-wrap">
          <h3>Write a Review</h3>
          <form onSubmit={submitReview} className="review-form">
            <div className="form-group">
              <label>Your Name</label>
              <input type="text" placeholder="Jane Doe" value={reviewForm.name} onChange={e => setReviewForm(form => ({ ...form, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Rating</label>
              <StarRating value={reviewForm.rating} onChange={rating => setReviewForm(form => ({ ...form, rating }))} />
            </div>
            <div className="form-group">
              <label>Your Review</label>
              <textarea placeholder="Share your experience with this product..." value={reviewForm.text} onChange={e => setReviewForm(form => ({ ...form, text: e.target.value }))} rows={4} />
            </div>
            {reviewError && <p className="field-error">{reviewError}</p>}
            <button type="submit" className="order-btn review-submit-btn" disabled={reviewSubmitting}>
              {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        </div>

        {reviewsLoading && <p className="reviews-empty">Loading reviews...</p>}

        {!reviewsLoading && reviews.length === 0 && (
          <p className="reviews-empty">No customer reviews yet.</p>
        )}

        {reviews.length > 0 && (
          <div className="reviews-list">
            {reviews.map(review => (
              <div key={review.id} className="review-item">
                <div className="review-header">
                  <strong>{review.name}</strong>
                  <StarRating value={Number(review.rating)} readOnly />
                  <span className="review-date">{review.date}</span>
                </div>
                <p className="review-text">{review.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && <div className="cart-toast">{toast}</div>}
      <Footer />
    </div>
  );
}

export default ProductDetail;
